<script setup>
import { ref, computed, watch } from 'vue'
import moment from 'moment'
import { router } from '@inertiajs/vue3'
import ReservationsSidePanel from '../components/ui-components/reservations/ReservationsSidePanel.vue'
import ReservationsCalendar from '../components/ui-components/reservations/ReservationsCalendar.vue'
import ReservationsSearchResults from '../components/ui-components/reservations/ReservationsSearchResults.vue'
import { useReservationsCalendar } from '../composables/useReservationsCalendar.js'
import { useSearch } from '../composables/useSearch.js'
import { useTemplateRef } from 'vue'
import { useReservationsTopbarStore } from '../../../state/reservationsTopbar.js'

const props = defineProps({
    reservations:        { type: Array,   default: () => [] },
    reservationForms:    { type: Array,   default: () => [] },
    allEmployees:        { type: Array,   default: () => [] },
    pendingChangeRequest:{ type: Array,   default: () => [] },
    mobileOnly:          { type: Boolean, default: false },
})

// Datum + pohled
const selectedDate = ref(moment())
const currentView  = ref('mesic')

const {
    visibleFormIds,
    visibleColleagueIds,
    toggleForm,
    toggleColleague,
} = useReservationsCalendar(props.reservationForms, props.allEmployees)

const searchQuery = ref('')
const { results: searchResults, isSearching } = useSearch(
    computed(() => props.reservations),
    ['name', 'email', 'phone', 'message', 'reservation_forms.name'],
    searchQuery
)

watch(isSearching, (val, prev) => {
    if (prev && !val) {
        router.visit('/app/reservations')
    }
})

// Navigace — delta se liší podle pohledu
const VIEW_DELTA = {
    den:    [1, 'day'],
    tyden:  [1, 'week'],
    mesic:  [1, 'month'],
    agenda: [1, 'month'],
    rok:    [1, 'year'],
}

function navigatePrev() {
    const [n, unit] = VIEW_DELTA[currentView.value] ?? [1, 'month']
    selectedDate.value = selectedDate.value.clone().subtract(n, unit)
}

function navigateNext() {
    const [n, unit] = VIEW_DELTA[currentView.value] ?? [1, 'month']
    selectedDate.value = selectedDate.value.clone().add(n, unit)
}

function goToday() {
    selectedDate.value = moment()
}

// Klik na měsíc v ročním přehledu → přepne na měsíční pohled
function selectMonth(date) {
    selectedDate.value = date
    currentView.value = 'mesic'
}

const calendarRef = useTemplateRef('calendarRef')
const topbarStore = useReservationsTopbarStore()
</script>

<template>
    <div class="flex h-dvh overflow-hidden">
        <ReservationsSidePanel
            :reservation-forms="reservationForms"
            :all-employees="allEmployees"
            :reservations="reservations"
            :visible-form-ids="visibleFormIds"
            :visible-colleague-ids="visibleColleagueIds"
            :current-month="selectedDate"
            :mobile-open="topbarStore.panelOpen"
            @toggle-form="toggleForm"
            @toggle-colleague="toggleColleague"
            @prev-month="navigatePrev"
            @next-month="navigateNext"
            @new-reservation="() => calendarRef?.openNewRes()"
            @update:search-query="searchQuery = $event"
            @close="topbarStore.closePanel()"
        />

        <div class="flex-1 flex flex-col pt-14 min-w-0 overflow-hidden">
            <ReservationsSearchResults
                v-if="isSearching"
                :results="searchResults"
                :query="searchQuery"
            />
            <ReservationsCalendar
                v-else
                ref="calendarRef"
                :reservations="reservations"
                :reservation-forms="reservationForms"
                :all-employees="allEmployees"
                :pending-change-request="pendingChangeRequest"
                :mobile-only="mobileOnly"
                :visible-form-ids="visibleFormIds"
                :visible-colleague-ids="visibleColleagueIds"
                :selected-date="selectedDate"
                :current-view="currentView"
                @prev="navigatePrev"
                @next="navigateNext"
                @today="goToday"
                @toggle-form="toggleForm"
                @toggle-colleague="toggleColleague"
                @update:current-view="currentView = $event"
                @select-month="selectMonth"
            />
        </div>
    </div>
</template>
